import type { BatchQueryEntry } from '@moment/horizon-data-access/platform'
import type { QuotePayload } from '@moment/sorm-client-data-access/schema'
import type { Ask, LiveInventoryItem } from '@/types/api/live-inventory'

import chunk from 'lodash/chunk'

import { createLogger } from '@/lib/logger'
import { SORM_MAX_QUERY_LIMIT } from '@/shared/sorm/filter-options-query'

import { RISK_GROUP_REQUEST_PARAM } from './security-wire-fields'

const logger = createLogger('shared/sorm/security/quote-books')

const QUOTE_ENTITY = 'Quote'

// Quote lives in the order-book context only; the default `clients` excludes it.
const QUOTE_CONTEXT = 'order_book'

/** Conservative quotes-per-ISIN estimate that sizes Quote entries under a page. */
const QUOTE_DEPTH_ESTIMATE = 50

/** ISINs per Quote entry; an entry that still fills a page is split again. */
const QUOTE_ISINS_PER_ENTRY = Math.floor(
  SORM_MAX_QUERY_LIMIT / QUOTE_DEPTH_ESTIMATE,
)

const QUOTE_FIELDS = [
  'isin',
  'side',
  'connection_id',
  'quote_id',
  'order_price',
  'order_ytw',
  'order_ytm',
  'quantity',
  'min_quantity',
  'min_increment',
] as const

export type QuoteRow = Pick<QuotePayload, (typeof QUOTE_FIELDS)[number]>

export type BondBook = Pick<LiveInventoryItem, 'asks' | 'bids'>

enum QuoteSide {
  Buy = 'SIDE_BUY',
  Sell = 'SIDE_SELL',
}

interface BookQuote {
  connectionKey: string
  quoteKey: string
  level: Ask
}

function toLevel(row: QuoteRow): Ask {
  return {
    price: Number(row.order_price),
    quantity: Number(row.quantity),
    min_quantity: Number(row.min_quantity),
    min_increment: row.min_increment,
    yield_to_worst: Number(row.order_ytw),
    yield_to_maturity: Number(row.order_ytm),
  }
}

function compareKeys(a: string, b: string): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

// SORM's merge_rank tie order: `concat(connection_id, ':')`, then `concat(quote_id, ':')`.
function compareTieKeys(a: BookQuote, b: BookQuote): number {
  return (
    compareKeys(a.connectionKey, b.connectionKey) ||
    compareKeys(a.quoteKey, b.quoteKey)
  )
}

function cheapestFirst(a: BookQuote, b: BookQuote): number {
  return a.level.price - b.level.price || compareTieKeys(a, b)
}

function richestFirst(a: BookQuote, b: BookQuote): number {
  return b.level.price - a.level.price || compareTieKeys(a, b)
}

/**
 * Each ISIN's book, one level per quote: asks cheapest first, bids richest
 * first, ties in SORM's order (venue, then quote id). The first level is then
 * the single best quote the marketplace's `best_ask_qty` / `best_bid_qty` report.
 */
export function buildBooks(rows: readonly QuoteRow[]): Map<string, BondBook> {
  const quotesByIsin = new Map<
    string,
    { asks: BookQuote[]; bids: BookQuote[] }
  >()
  for (const row of rows) {
    const quotes = quotesByIsin.get(row.isin) ?? { asks: [], bids: [] }
    quotesByIsin.set(row.isin, quotes)
    const quote = {
      connectionKey: `${row.connection_id}:`,
      quoteKey: `${row.quote_id}:`,
      level: toLevel(row),
    }
    switch (row.side) {
      case QuoteSide.Sell:
        quotes.asks.push(quote)
        break
      case QuoteSide.Buy:
        quotes.bids.push(quote)
        break
      default:
        logger.warn('Unknown quote side', { side: row.side })
    }
  }
  return new Map(
    [...quotesByIsin].map(([isin, { asks, bids }]) => [
      isin,
      {
        asks: asks.sort(cheapestFirst).map(({ level }) => level),
        bids: bids.sort(richestFirst).map(({ level }) => level),
      },
    ]),
  )
}

/** One batch entry reading the merge-eligible order-book quotes of `isins`. */
export function quoteBookBatchEntry(
  id: string,
  isins: readonly string[],
  riskGroupId: string,
): BatchQueryEntry {
  return {
    id,
    query: {
      root_entity: QUOTE_ENTITY,
      context: QUOTE_CONTEXT,
      fields: QUOTE_FIELDS.map((field) => ({
        entity: QUOTE_ENTITY,
        field,
        alias: field,
      })),
      filters: {
        'Quote.isin': { in: [...isins] },
        // Merge-eligible quotes only; SORM has no `is_not_null`.
        'Quote.merge_rank': { greater_than: 0 },
      },
      sort: ['isin', 'side', 'connection_id', 'quote_id'].map((field) => ({
        entity: QUOTE_ENTITY,
        field,
      })),
      limit: SORM_MAX_QUERY_LIMIT,
      request_params: { [RISK_GROUP_REQUEST_PARAM]: riskGroupId },
    },
  }
}

/** The ISIN chunks a book read starts from. */
export function quoteIsinChunks(isins: readonly string[]): string[][] {
  return chunk([...new Set(isins)], QUOTE_ISINS_PER_ENTRY)
}

/** Whether a Quote entry's rows may have been truncated at the page limit. */
export function isQuotePageFull(rows: readonly unknown[]): boolean {
  return rows.length >= SORM_MAX_QUERY_LIMIT
}
